import { CartAddEvent, ThemeEvents } from '@theme/events';

/*
 * Free gift offers for Horizon 3.x. Offers come from Theme settings > Free gift offers.
 * <gift-offers>: "Add both to cart" buttons on the Offers page.
 * <gift-note>: product page note; after the product form adds this product, it adds the free product once the
 * cart holds the qualifying quantity. The matching automatic "Buy X get Y" discount makes the gift free.
 */

const GIFT_SOURCE = 'gift-offers';

/**
 * Adds lines to the cart and tells the header cart icon (and any other listener) about it.
 * @param {HTMLElement} origin
 * @param {{ id: number, quantity: number }[]} items
 */
async function addCartLines(origin, items) {
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error(`Cart add failed (${response.status})`);

  const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
  origin.dispatchEvent(
    new CartAddEvent(cart, origin.id || GIFT_SOURCE, {
      source: GIFT_SOURCE,
      itemCount: cart.item_count,
    })
  );

  // Shiprocket (Fastrr) smart cart keeps its own copy of the cart; redraw it so the gift shows
  try {
    await window.updateMiniCartSection?.({ updatedCart: cart });
  } catch (error) {
    console.warn('[gift-offers] Smart cart refresh failed:', error);
  }
  return cart;
}

/**
 * Whether the cart qualifies for an offer and doesn't hold its gift yet.
 * With a buy variant only that variant counts; otherwise any variant of the buy product counts.
 * @param {{ product_id: number, variant_id: number, quantity: number }[]} items
 * @param {{ buyProduct: number, buyVariant: number, freeProduct: number, freeVariant: number, required: number }} offer
 */
function needsGift(items, offer) {
  const qualifies = (/** @type {{ product_id: number, variant_id: number }} */ item) =>
    offer.buyVariant ? item.variant_id === offer.buyVariant : item.product_id === offer.buyProduct;
  const qualifying = items.filter(qualifies).reduce((sum, item) => sum + item.quantity, 0);
  if (qualifying < offer.required) return false;

  // When the gift itself counts towards the offer (same variant, or same product with no buy variant),
  // units above the required quantity are the gift
  const giftCounts = offer.buyVariant ? offer.freeVariant === offer.buyVariant : offer.freeProduct === offer.buyProduct;
  if (giftCounts) return qualifying === offer.required;
  return !items.some((item) => item.variant_id === offer.freeVariant);
}

/**
 * Shows the cart after an offer is added: the Shiprocket (Fastrr) smart cart when it's installed,
 * else the theme's cart drawer.
 */
function openCartDrawer() {
  const smartCart = /** @type {any} */ (window).slideShiprocketSmartCartInFrame;
  if (typeof smartCart === 'function') {
    smartCart(true);
    return;
  }
  /** @type {any} */ (document.querySelector('cart-drawer-component'))?.open?.();
}

/**
 * Announces finished cart requests (from the theme or any app, fetch or XHR) as a `cart-request:done` event
 * on document. Installed once per page even when several scripts include it.
 */
function watchCartRequests() {
  const win = /** @type {any} */ (window);
  if (win.__cartRequestWatch) return;
  win.__cartRequestWatch = true;

  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const notify = (/** @type {string} */ url) => {
    try {
      const path = new URL(url, window.location.origin).pathname;
      if (CART_WRITE.test(path)) document.dispatchEvent(new CustomEvent('cart-request:done', { detail: { path } }));
    } catch {
      // Not a URL we can read
    }
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = originalFetch.apply(this, args);
    const input = /** @type {any} */ (args[0]);
    const url = typeof input === 'string' ? input : input?.url ?? String(input);
    request.then(
      () => notify(url),
      () => {}
    );
    return request;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (/** @type {string} */ method, /** @type {string | URL} */ url, ...rest) {
    this.addEventListener('loadend', () => notify(String(url)));
    return /** @type {any} */ (originalOpen).call(this, method, url, ...rest);
  };
}

class GiftOffers extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-buy-variant]') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    const buyId = Number(button.dataset.buyVariant);
    const freeId = Number(button.dataset.freeVariant);
    const quantity = Math.max(1, Number(button.dataset.buyQuantity) || 1);
    if (!buyId || !freeId) return;

    // Same product for both (e.g. buy 2 wipes, get 1 free): one line of quantity + 1
    const items = buyId === freeId ? [{ id: buyId, quantity: quantity + 1 }] : [{ id: buyId, quantity }, { id: freeId, quantity: 1 }];
    const label = button.querySelector('[data-label]');
    const original = label?.textContent ?? '';

    button.setAttribute('aria-busy', 'true');
    try {
      await addCartLines(button, items);
      button.dataset.added = '';
      if (label) label.textContent = button.dataset.addedText || original;
      openCartDrawer();
    } catch (error) {
      console.error('[gift-offers] Offer add failed:', error);
      if (label) label.textContent = button.dataset.errorText || original;
    } finally {
      button.removeAttribute('aria-busy');
      window.setTimeout(() => {
        delete button.dataset.added;
        if (label) label.textContent = original;
      }, 2500);
    }
  };
}

class GiftNote extends HTMLElement {
  /** Time until which a cart add counts as the shopper's add-to-cart (ms timestamp) */
  #armedUntil = 0;
  #checking = false;

  connectedCallback() {
    // Cart apps (slide carts, checkout apps) can take over the add-to-cart, so don't rely on theme events alone:
    // arm on the shopper's add-to-cart click, then react to the cart/add request itself.
    document.addEventListener('click', this.#onClick, true);
    document.addEventListener('submit', this.#onSubmit, true);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    watchCartRequests();
    document.addEventListener('cart-request:done', this.#onCartRequest);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onClick, true);
    document.removeEventListener('submit', this.#onSubmit, true);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('cart-request:done', this.#onCartRequest);
  }

  /** @param {Event & { detail?: { path?: string } }} event */
  #onCartRequest = (event) => {
    if (/\/cart\/add/.test(event.detail?.path ?? '') && Date.now() < this.#armedUntil) this.#check();
  };

  #arm() {
    this.#armedUntil = Date.now() + 15000;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[name="add"], button[type="submit"], .add-to-cart-button');
    if (button?.closest('product-form-component, form[action*="/cart/add"]')) this.#arm();
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement && event.target.action.includes('/cart/add')) this.#arm();
  };

  /** @param {Event & { detail?: any }} event */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (!data || data.didError || data.source !== 'product-form-component') return;
    if (String(data.productId) !== this.dataset.buyProduct) return;
    this.#check();
  };

  async #check() {
    if (this.#checking) return;
    this.#checking = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      const offer = {
        buyProduct: Number(this.dataset.buyProduct),
        buyVariant: Number(this.dataset.buyVariant) || 0,
        freeProduct: Number(this.dataset.freeProduct),
        freeVariant: Number(this.dataset.freeVariant),
        required: Math.max(1, Number(this.dataset.buyQuantity) || 1),
      };
      if (!offer.freeVariant || !needsGift(cart.items, offer)) return;

      this.#armedUntil = 0;
      await addCartLines(this, [{ id: Number(this.dataset.freeVariant), quantity: 1 }]);
    } catch (error) {
      console.error('[gift-offers] Gift add failed:', error);
    } finally {
      this.#checking = false;
    }
  }
}

if (!customElements.get('gift-offers')) customElements.define('gift-offers', GiftOffers);
if (!customElements.get('gift-note')) customElements.define('gift-note', GiftNote);
